/**
 * Specs Q&A Agent
 * Calls backend /api/specs-agent for secure Gemini access with streaming
 * API key is kept server-side only!
 */

import { supabase } from '../lib/supabase';

export interface SpecsCitation {
  // Multi-file support
  fileId?: string;      // Database ID of the specs file (for multi-file queries)
  fileName?: string;    // Display name of the file (for multi-file queries)
  
  // Original fields
  pageNumber: number;
  quote: string;
  section?: string;
  analysis?: string;
  boundingBox?: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
}

export interface SpecsResponse {
  answer: string;
  citations: SpecsCitation[];
  confidence: 'high' | 'medium' | 'low';
  thinkingSteps: Array<{
    step: string;
    pagesSearched?: number[];
    result?: string;
  }>;
}

/**
 * Query specifications using backend API with streaming
 */
export async function querySpecifications(
  fileUri: string,
  question: string,
  specsFileId: string,
  onThinkingStep?: (step: { step: string; status: 'starting' | 'completed' | 'error'; result?: string }) => void
): Promise<SpecsResponse> {
  
  try {
    // Step 1: Get or create Gemini file URI (via backend for File API)
    onThinkingStep?.({ step: 'Loading specifications...', status: 'starting' });
    
    const uploadResponse = await fetch('/api/upload-specs-to-gemini', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileUri, specsFileId })
    });
    
    if (!uploadResponse.ok) {
      throw new Error('Failed to get Gemini file URI');
    }
    
    const { geminiFileUri } = await uploadResponse.json();
    console.log('✅ Got Gemini file URI:', geminiFileUri);
    
    // Step 2: Call backend specs-agent with streaming
    onThinkingStep?.({ step: 'Connecting to AI...', status: 'starting' });
    
    const response = await fetch('/api/specs-agent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        question,
        geminiFileUris: [{
          geminiFileUri,
          fileName: specsFileId,
          fileId: specsFileId
        }]
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to query specs');
    }

    // Handle SSE streaming response
    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let result: SpecsResponse | null = null;
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      
      // Process complete SSE events
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || ''; // Keep incomplete event in buffer

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        
        try {
          const event = JSON.parse(line.slice(6));
          
          switch (event.type) {
            case 'thought':
              console.log('💭 Thought:', event.data);
              onThinkingStep?.({ step: event.data, status: 'starting' });
              break;
            case 'status':
              console.log('📊 Status:', event.data);
              onThinkingStep?.({ step: event.data, status: 'starting' });
              break;
            case 'result':
              result = event.data;
              break;
            case 'error':
              throw new Error(event.data);
            case 'done':
              console.log('✅ Stream complete');
              break;
          }
        } catch (e) {
          // Ignore JSON parse errors for incomplete events
          if (line.includes('"type"')) {
            console.warn('Failed to parse SSE event:', line, e);
          }
        }
      }
    }

    if (!result) {
      throw new Error('No result received from specs agent');
    }

    // Show final search steps
    if (result.thinkingSteps) {
      for (const step of result.thinkingSteps) {
        onThinkingStep?.({ 
          step: step.step, 
          status: 'completed',
          result: step.result
        });
      }
    }

    return result;
    
  } catch (error: any) {
    console.error('Error querying specs:', error);
    onThinkingStep?.({ 
      step: 'Error searching specifications', 
      status: 'error',
      result: error?.message || String(error)
    });
    throw error;
  }
}

/**
 * Query multiple specification files concurrently
 * Each file is queried separately, then results are synthesized
 */
export async function queryMultipleSpecifications(
  specsFiles: Array<{
    id: string;
    file_name: string;
    storage_path: string;
  }>,
  question: string,
  onThinkingStep?: (step: { step: string; status: 'starting' | 'completed' | 'error'; result?: string }) => void
): Promise<SpecsResponse> {
  
  if (specsFiles.length === 0) {
    throw new Error('No specification files provided');
  }

  // If only one file, use the single-file query function
  if (specsFiles.length === 1) {
    const file = specsFiles[0];
    if (!supabase) throw new Error('Supabase not configured');
    
    const { data } = await supabase.storage
      .from('bucket')
      .getPublicUrl(file.storage_path);
    
    const result = await querySpecifications(
      data.publicUrl,
      question,
      file.id,
      onThinkingStep
    );
    
    // Tag citations with file info
    return {
      ...result,
      citations: result.citations.map(c => ({
        ...c,
        fileId: file.id,
        fileName: file.file_name
      }))
    };
  }

  try {
    // Step 1: Upload all files to Gemini (with caching)
    onThinkingStep?.({ 
      step: `Loading ${specsFiles.length} specification files...`, 
      status: 'starting' 
    });
    
    if (!supabase) throw new Error('Supabase not configured');

    const geminiFilePromises = specsFiles.map(async (file) => {
      const { data } = await supabase!.storage
        .from('bucket')
        .getPublicUrl(file.storage_path);
      
      // Get or create Gemini file URI (cached!)
      const response = await fetch('/api/upload-specs-to-gemini', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          fileUri: data.publicUrl, 
          specsFileId: file.id 
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to upload ${file.file_name} to Gemini`);
      }
      
      const { geminiFileUri } = await response.json();
      console.log(`✅ Got Gemini URI for ${file.file_name}`);
      
      return { 
        geminiFileUri, 
        fileName: file.file_name,
        fileId: file.id
      };
    });
    
    const geminiFileUris = await Promise.all(geminiFilePromises);
    
    // Step 2: Call backend specs-agent with all files
    onThinkingStep?.({ 
      step: `Searching across ${specsFiles.length} specification files...`, 
      status: 'starting' 
    });
    
    const response = await fetch('/api/specs-agent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        question,
        geminiFileUris
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to query specs');
    }

    // Handle SSE streaming response
    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let result: SpecsResponse | null = null;
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      
      // Process complete SSE events
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
      
      try {
          const event = JSON.parse(line.slice(6));
          
          switch (event.type) {
            case 'thought':
              console.log('💭 Thought:', event.data);
              onThinkingStep?.({ step: event.data, status: 'starting' });
              break;
            case 'status':
              console.log('📊 Status:', event.data);
              onThinkingStep?.({ step: event.data, status: 'starting' });
              break;
            case 'result':
              result = event.data;
              break;
            case 'error':
              throw new Error(event.data);
            case 'done':
              console.log('✅ Stream complete');
              break;
          }
        } catch (e) {
          if (line.includes('"type"')) {
            console.warn('Failed to parse SSE event:', line, e);
          }
        }
      }
    }

    if (!result) {
      throw new Error('No result received from specs agent');
    }

    // Show final search steps
    if (result.thinkingSteps) {
      for (const step of result.thinkingSteps) {
    onThinkingStep?.({ 
          step: step.step, 
          status: 'completed',
          result: step.result
        });
      }
    }

    return result;
    
  } catch (error: any) {
    console.error('Error querying multiple specs:', error);
    onThinkingStep?.({ 
      step: 'Error searching specifications', 
      status: 'error',
      result: error?.message || String(error)
    });
    throw error;
  }
}
